/**
 * SuperAdminStudio — Studio Control Center.
 *
 * Single-page governance + analytics console for the public Chef Studio
 * ecosystem. Wraps the existing /api/studio/config + /api/studio/admin/stats
 * endpoints — NO new backend logic.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ChefHat, Users, BookOpen, Tag, Activity, ExternalLink, Save, Sparkles,
  ShieldCheck, Smartphone, Share2, MessageSquare, RefreshCw,
} from 'lucide-react';
import { fmtDateTimeIST } from '../lib/tz';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function StatCard({ Icon, label, value, sub, accent = 'bg-[#FCE7D6] text-[#E8643E]', testId }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${accent}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-[#3A1F12] tabular-nums mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-stone-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function ToggleRow({ label, description, value, onChange, testId }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-stone-100 last:border-0">
      <div className="flex-1 pr-3">
        <p className="text-sm font-semibold text-[#3A1F12]">{label}</p>
        {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
      </div>
      <button type="button" onClick={() => onChange(!value)}
        data-testid={testId}
        className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-[#E8643E]' : 'bg-stone-300'}`}>
        <span className={`absolute top-0.5 ${value ? 'right-0.5' : 'left-0.5'} w-4 h-4 bg-white rounded-full shadow-md transition-all`} />
      </button>
    </div>
  );
}

function NumberRow({ label, description, value, onChange, suffix, min = 0, max = 100000, testId }) {
  return (
    <div className="py-3 border-b border-stone-100 last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#3A1F12]">{label}</p>
          {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Input type="number" value={value ?? 0} min={min} max={max}
            onChange={(e) => onChange(Number(e.target.value))}
            data-testid={testId}
            className="h-8 w-24 text-right tabular-nums bg-white border-stone-200 text-[#3A1F12]" />
          {suffix && <span className="text-[10px] uppercase tracking-wider text-stone-500">{suffix}</span>}
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminStudio() {
  const { getHeaders } = useAuth();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/studio/config`, { headers: getHeaders() }),
        axios.get(`${API}/studio/admin/stats`, { headers: getHeaders() }),
      ]);
      setConfig(c.data);
      setDraft(c.data);
      setStats(s.data);
    } catch (e) {
      toast.error('Failed to load Studio control center');
    } finally {
      setRefreshing(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(config);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/studio/config`, draft, { headers: getHeaders() });
      setConfig(data);
      setDraft(data);
      toast.success('Studio configuration updated');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  if (!config || !stats) {
    return <div className="text-center py-20 text-stone-500">Loading…</div>;
  }

  return (
    <div data-testid="super-studio-page" className="space-y-6">
        {/* Privacy notice — clarify the trust model */}
        <div className="rounded-lg bg-emerald-50/60 border border-emerald-200/70 px-3.5 py-2.5 flex items-start gap-2.5"
          data-testid="super-studio-privacy-banner">
          <ShieldCheck className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
          <div>
            <p className="text-[12px] font-semibold text-emerald-900">Creator privacy by default</p>
            <p className="text-[11px] text-emerald-800/85 leading-snug">
              You see usage analytics, sign-up activity and workspace health — never the contents of a creator's private recipes. Recipes become visible only when a chef explicitly publishes them (Phase 2: Public / Shared / Community).
            </p>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#FCE7D6] mb-2">
              <Sparkles className="w-3 h-3 text-[#E8643E]" />
              <span className="text-[9px] uppercase tracking-wider text-[#7A2A12] font-bold">Studio Control Center</span>
            </div>
            <h1 className="text-2xl font-bold text-[#3A1F12] tracking-tight">Chef Studio — ecosystem governance</h1>
            <p className="text-sm text-stone-600 mt-1">
              Public landing: <a href="/studio" target="_blank" rel="noopener noreferrer"
                data-testid="studio-public-link"
                className="text-[#E8643E] hover:text-[#C45A41] underline underline-offset-2 inline-flex items-center gap-1 font-medium">
                {window.location.origin}/studio <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/studio/login" target="_blank" rel="noopener noreferrer"
              data-testid="studio-test-login-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#FCE7D6] hover:bg-[#FAD9C0] text-[#7A2A12] text-[12px] font-semibold border border-[#F4B860]/40 transition-colors">
              <Smartphone className="w-3.5 h-3.5" /> Test Studio Login
            </Link>
            <Link to="/studio" target="_blank" rel="noopener noreferrer"
              data-testid="studio-open-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white hover:bg-stone-50 text-[#3A1F12] text-[12px] font-semibold border border-stone-200 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> Open Studio
            </Link>
            <button type="button" onClick={load} disabled={refreshing}
              data-testid="super-studio-refresh"
              className="text-stone-500 hover:text-[#3A1F12] p-2"
              title="Refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Analytics */}
        <div data-testid="super-studio-analytics" className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard Icon={Users} label="Studio users"
            value={stats.user_count} sub={`${stats.workspace_count} workspaces`}
            accent="bg-[#FCE7D6] text-[#E8643E]"
            testId="stat-users" />
          <StatCard Icon={Activity} label="Active (30d)"
            value={stats.active_30d} sub="recipes touched"
            accent="bg-emerald-50 text-emerald-600"
            testId="stat-active" />
          <StatCard Icon={BookOpen} label="Total recipes"
            value={stats.recipe_total}
            accent="bg-blue-50 text-blue-600"
            testId="stat-recipes" />
          <StatCard Icon={Tag} label="Priced recipes"
            value={stats.pricing_total} sub="menu price set"
            accent="bg-purple-50 text-purple-600"
            testId="stat-pricing" />
          <StatCard Icon={ChefHat} label="Modules live"
            value={(config.modules || []).length}
            sub={(config.modules || []).slice(0, 3).join(' · ')}
            accent="bg-pink-50 text-pink-600"
            testId="stat-modules" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Config (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm" data-testid="super-studio-config-access">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-[#E8643E]" />
                <h3 className="text-sm font-bold text-[#3A1F12]">Access &amp; authentication</h3>
              </div>
              <ToggleRow label="Studio enabled"
                description="Master switch — when off, public landing + OTP endpoints are disabled."
                value={!!draft.studio_enabled}
                onChange={(v) => set('studio_enabled', v)}
                testId="toggle-studio-enabled" />
              <ToggleRow label="OTP login enabled"
                description="Disable to temporarily block new public signups while keeping existing workspaces alive."
                value={!!draft.otp_enabled}
                onChange={(v) => set('otp_enabled', v)}
                testId="toggle-otp-enabled" />
            </div>

            <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm" data-testid="super-studio-config-limits">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-[#E8643E]" />
                <h3 className="text-sm font-bold text-[#3A1F12]">Workspace usage caps</h3>
              </div>
              <NumberRow label="Max recipes per workspace" description="Free-tier ceiling. 0 = unlimited."
                value={draft.max_recipes_per_workspace}
                onChange={(v) => set('max_recipes_per_workspace', v)}
                suffix="recipes" testId="input-max-recipes" />
              <NumberRow label="Max ingredients per workspace" description="Free-tier ingredient cap."
                value={draft.max_ingredients_per_workspace}
                onChange={(v) => set('max_ingredients_per_workspace', v)}
                suffix="items" testId="input-max-ingredients" />
              <NumberRow label="Workspace expiry" description="Days before inactive workspace is archived. 0 = never."
                value={draft.workspace_expiry_days}
                onChange={(v) => set('workspace_expiry_days', v)}
                suffix="days" testId="input-expiry" />
            </div>

            <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm" data-testid="super-studio-config-sharing">
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-4 h-4 text-[#E8643E]" />
                <h3 className="text-sm font-bold text-[#3A1F12]">Sharing &amp; community (Phase 2)</h3>
              </div>
              <ToggleRow label="Public recipe sharing"
                description="Allow chefs to publish recipes to a public URL."
                value={!!draft.public_sharing_enabled}
                onChange={(v) => set('public_sharing_enabled', v)}
                testId="toggle-public-sharing" />
              <ToggleRow label="Community recipe access"
                description="Discover & clone recipes from other creators."
                value={!!draft.community_access_enabled}
                onChange={(v) => set('community_access_enabled', v)}
                testId="toggle-community-access" />
            </div>

            <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm" data-testid="super-studio-config-sms">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-[#E8643E]" />
                <h3 className="text-sm font-bold text-[#3A1F12]">SMS provider</h3>
              </div>
              <p className="text-xs text-stone-600">
                <span className="text-[#7A2A12] font-semibold">Public Beta mode</span> — Studio uses a fixed beta OTP shared on the login UI for frictionless onboarding while we validate traction. Set <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded text-[#7A2A12]">STUDIO_AUTH_MODE=live</code> + <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded text-[#7A2A12]">MSG91_AUTHKEY</code>/<code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded text-[#7A2A12]">MSG91_TEMPLATE_ID</code> in backend <code className="text-[10px] bg-stone-100 px-1 py-0.5 rounded text-[#7A2A12]">.env</code> to switch on real SMS delivery — no other code changes needed.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setDraft(config)} disabled={!dirty}
                className="text-stone-500 hover:text-[#3A1F12]">
                Discard changes
              </Button>
              <Button size="sm" onClick={save} disabled={saving || !dirty}
                data-testid="super-studio-save"
                className="bg-[#E8643E] hover:bg-[#C45A41] text-white font-semibold">
                <Save className="w-3.5 h-3.5 mr-1" />
                {saving ? 'Saving…' : 'Save configuration'}
              </Button>
            </div>
          </div>

          {/* Recent signups (1 col) */}
          <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm lg:col-span-1" data-testid="super-studio-recent-signups">
            <h3 className="text-sm font-bold text-[#3A1F12] mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#E8643E]" /> Recent signups
            </h3>
            {(stats.recent_signups || []).length === 0 ? (
              <p className="text-xs text-stone-500 text-center py-6">No signups yet.</p>
            ) : (
              <ul className="space-y-2">
                {stats.recent_signups.map(t => (
                  <li key={t.tenant_id} className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2">
                    <p className="text-xs font-semibold text-[#3A1F12] truncate">{t.user_name || t.name || '—'}</p>
                    <p className="text-[10px] text-stone-600 tabular-nums">{t.phone || '—'}</p>
                    <p className="text-[10px] text-stone-400 tabular-nums">{t.created_at ? fmtDateTimeIST(t.created_at) : '—'}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
  );
}
